import React from 'react';

const PATH = import.meta.env.VITE_UNIVERSAL_STATIC_URL;

/**
 * Минимальный тип пункта info-меню (дропдаун Teacher) в aa-header.
 * Структура совместима с antd ItemType, который ожидает aa-header, но не тянет
 * внутренние типы antd/kit (в creative-hub antd не является прямой зависимостью).
 */
export type HeaderInfoMenuItem = {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick?: () => void;
};

type WindowWithGlobalVars = {
  AAHeader?: {
    render: (options: {
      service: { key?: string };
      addFonts?: boolean;
      addAnt?: boolean;
      infoMenuItems?: HeaderInfoMenuItem[];
    }) => void;
    selectService: (...args: any) => void;
  };
  MyuaHeader?: { render: (...args: any) => void };
};

const service: { key?: string } = { key: undefined };

// Контейнер иконок-триггеров в шапке (в него aa-header кладёт "?", избранное и т.п.).
// Свои иконки монтируются порталом внутрь него: они встают в общий grid рядом с
// остальными (не нужны ни fixed-позиционирование, ни подгонка отступов) и попадают
// в stacking context шапки. Последнее важно: у .aa-header всего z-index: 1, поэтому
// собственный fixed-элемент с большим z-index перекрывал бы модалки и лайтбоксы
// приложения (MYUA-16416).
export const getHeaderMenuTriggers = () =>
  document.querySelector<HTMLElement>('.aa-header-menu-triggers');

// Подписка на факт появления шапки: элементы, живущие в её зоне (например,
// индикатор slow network), не должны появляться раньше неё (MYUA-16416).
let headerLoaded = false;
const headerLoadedCallbacks = new Set<() => void>();

export const isHeaderLoaded = () => headerLoaded;

export const onHeaderLoaded = (callback: () => void) => {
  headerLoadedCallbacks.add(callback);
  return () => {
    headerLoadedCallbacks.delete(callback);
  };
};

const HEADER_MOUNT_POLL_MS = 100;
const HEADER_MOUNT_TIMEOUT_MS = 30000;

// AAHeader.render() рендерит шапку своим React-рутом, а коммит у concurrent-рута
// происходит не синхронно с вызовом — DOM шапки появляется уже после onload
// скрипта. Поэтому ждём сам контейнер иконок, а не факт загрузки файла.
const notifyWhenHeaderMounted = () => {
  const deadline = performance.now() + HEADER_MOUNT_TIMEOUT_MS;
  const timer = setInterval(() => {
    if (!getHeaderMenuTriggers()) {
      if (performance.now() > deadline) {
        clearInterval(timer);
        console.warn('aa-header menu triggers container did not appear');
      }
      return;
    }
    clearInterval(timer);
    headerLoaded = true;
    headerLoadedCallbacks.forEach((callback) => callback());
  }, HEADER_MOUNT_POLL_MS);
};

export const initHeader = ({
  key,
  infoMenuItems,
}: {
  key: string;
  infoMenuItems?: HeaderInfoMenuItem[];
}) => {
  service.key = key;
  const script = document.createElement('script');
  // Cache-busting: u-static отдаёт aa-header.js без Cache-Control (только ETag/
  // Last-Modified), из-за чего браузер кеширует его эвристически и пользователи
  // зависают на старой версии render()/InfoMenu. ?v=<build hash> меняет ключ
  // браузерного кеша при каждом деплое → принудительно подтягивается свежий файл.
  // Проверено: u-static не режет/не нормализует query (отдаёт тот же файл на ?v=),
  // edge-CDN в цепочке нет — фикс полностью на стороне этого репо.
  script.src = `${PATH}/aa-header/aa-header.js?v=${__BUILD_HASH__}`;
  script.onload = () => {
    (window as WindowWithGlobalVars).AAHeader?.render({
      service,
      addFonts: true,
      addAnt: true,
      // Поле опциональное: старый aa-header.js на CDN просто проигнорирует незнакомое
      // свойство (JS не ругается на лишние поля объекта опций) — graceful degradation.
      ...(infoMenuItems ? { infoMenuItems } : {}),
    });
    notifyWhenHeaderMounted();
  };
  document.head.appendChild(script);
};

export const selectService = (key: string) => {
  service.key = key;
  (window as WindowWithGlobalVars).AAHeader?.selectService(service);
};
